import type { Node, Edge } from '../../../core/models/scene';

type FlowchartNode = Node & { id: string };

const isFlowchartNode = (n: Node): n is FlowchartNode => {
  const t = (n as any)?.type as string | undefined;
  return (
    t === 'process' ||
    t === 'decision' ||
    t === 'terminator' ||
    t === 'data' ||
    t === 'document' ||
    t === 'flowchart-shape' ||
    t === 'bpmn-shape'
  );
};

const isFlowchartEdge = (e: Edge): boolean => {
  const t = (e as any)?.type as string | undefined;
  if (t === 'sequence_flow') return true;
  return (e as any)?.category === 'flowchart' && (e as any)?.type === 'line';
};

export const getConnectedFlowchartNodeIds = (
  nodes: Node[],
  edges: Edge[],
  seedNodeIds: string[]
): string[] => {
  const flowNodes = nodes.filter(isFlowchartNode);
  const allowed = new Set(flowNodes.map((n) => n.id));
  if (allowed.size === 0) return [];

  const adjacency = new Map<string, Set<string>>();
  const addAdj = (a: string, b: string) => {
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    adjacency.get(a)!.add(b);
  };

  for (const e of edges) {
    if (!isFlowchartEdge(e)) continue;
    const from = (e as any).from as string;
    const to = (e as any).to as string;
    if (!from || !to) continue;
    if (!allowed.has(from) || !allowed.has(to)) continue;
    addAdj(from, to);
    addAdj(to, from);
  }

  const queue: string[] = [];
  const visited = new Set<string>();
  for (const id of seedNodeIds) {
    if (allowed.has(id) && !visited.has(id)) {
      visited.add(id);
      queue.push(id);
    }
  }
  if (queue.length === 0) return [];

  while (queue.length > 0) {
    const cur = queue.shift()!;
    const neigh = adjacency.get(cur);
    if (!neigh) continue;
    for (const nxt of neigh) {
      if (visited.has(nxt)) continue;
      visited.add(nxt);
      queue.push(nxt);
    }
  }

  return Array.from(visited);
};

